
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Package, Recycle, TrendingUp, Leaf, Building, CheckCircle } from 'lucide-react';
import ValorisationPageLayout from '@/components/valorisation/ValorisationPageLayout';
import ValorisationContentSection from '@/components/valorisation/ValorisationContentSection';

const DIB = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Recyclage DIB - Déchets Industriels Banals",
    "description": "Service professionnel de collecte et recyclage des déchets industriels banals. Solutions complètes pour entreprises et professionnels.",
    "provider": {
      "@type": "Organization",
      "name": "Groupe Fabre Environnement",
      "url": "https://www.groupefabre-environnement.fr"
    },
    "areaServed": ["Occitanie", "Auvergne-Rhône-Alpes", "Île-de-France"],
    "serviceType": "Recyclage DIB",
    "offers": {
      "@type": "Offer",
      "description": "Collecte et recyclage de tous types de déchets industriels banals"
    }
  };

  const breadcrumbItems = [
    { label: "Accueil", href: "/" },
    { label: "Valorisation", href: "/valorisation" },
    { label: "DIB", href: "/valorisation/dib", current: true }
  ];

  const contentBlocks = [
    {
      type: 'text' as const,
      title: "Qu'est-ce que les DIB ?",
      content: "Les DIB (Déchets Industriels Banals) sont des déchets non dangereux produits par les entreprises, les commerces et les services. Ils regroupent une grande variété de matériaux recyclables qui nécessitent une gestion spécialisée pour optimiser leur valorisation et respecter la réglementation environnementale.",
      variant: 'highlighted' as const
    },
    {
      type: 'list' as const,
      title: "Types de DIB que nous collectons",
      items: [
        "Déchets de bureau : papiers, cartons, mobilier professionnel",
        "Emballages industriels : palettes, films plastiques, cartons",
        "Déchets de production : chutes de matériaux, rebuts manufacturés",
        "Matériaux de construction : gravats, bois, métaux de chantier",
        "Équipements mobiliers : mobilier de bureau, rayonnages",
        "Textiles professionnels : vêtements de travail, tissus",
        "Déchets verts d'espaces professionnels",
        "Déchets de restauration collective (hors déchets organiques)"
      ]
    },
    {
      type: 'process' as const,
      title: "Notre processus de gestion des DIB",
      process: [
        {
          step: 1,
          title: "Audit et diagnostic",
          description: "Évaluation des flux de déchets et analyse des besoins de votre entreprise"
        },
        {
          step: 2,
          title: "Collecte adaptée",
          description: "Mise en place de solutions de collecte sur mesure selon vos contraintes"
        },
        {
          step: 3,
          title: "Tri et préparation",
          description: "Séparation des matériaux et préparation pour optimiser la valorisation"
        },
        {
          step: 4,
          title: "Orientation filières",
          description: "Acheminement vers les filières de recyclage appropriées"
        },
        {
          step: 5,
          title: "Traçabilité complète",
          description: "Suivi documenté et reporting de la valorisation de vos déchets"
        }
      ]
    },
    {
      type: 'benefits' as const,
      title: "Les avantages de notre service DIB",
      benefits: [
        {
          title: "Conformité réglementaire",
          description: "Respect des obligations légales de tri et de valorisation des déchets d'entreprise",
          icon: Building
        },
        {
          title: "Économie circulaire",
          description: "Maximisation du recyclage et réduction de l'enfouissement des déchets",
          icon: Recycle
        },
        {
          title: "Optimisation des coûts",
          description: "Réduction des coûts de gestion des déchets par une approche globale",
          icon: TrendingUp
        }
      ],
      variant: 'success' as const
    },
    {
      type: 'cta' as const,
      title: "Optimisez la gestion de vos DIB",
      content: "Nos experts vous accompagnent dans la mise en place d'une solution complète de gestion de vos déchets industriels banals.",
      ctaText: "Demander un audit gratuit",
      ctaLink: "/contact",
      variant: 'highlighted' as const
    }
  ];

  return (
    <ValorisationPageLayout
      title="Recyclage DIB - Déchets Industriels Banals | Groupe Fabre Environnement"
      description="Collecte et recyclage des déchets industriels banals. Solutions professionnelles de gestion des DIB pour entreprises et collectivités."
      canonical="/valorisation/dib"
      keywords="recyclage DIB, déchets industriels banals, gestion déchets entreprise, valorisation déchets"
      structuredData={structuredData}
      heroTitle="DIB"
      heroSubtitle="Gestion complète des déchets industriels banals"
      breadcrumbItems={breadcrumbItems}
    >
      <ValorisationContentSection
        title="Recyclage des déchets industriels banals"
        subtitle="Solutions sur mesure pour la valorisation de vos DIB d'entreprise"
        blocks={contentBlocks}
        className="bg-white"
      />
    </ValorisationPageLayout>
  );
};

export default DIB;
